import { ROLE_COLORS } from "./RoleColorPicker";

export const jobDraft = (job) => ({ uuid: job.uuid, excluded: job.excluded, role_id: job.role_id, new_role: null, ...(job.import_permission ? { import_permission: job.import_permission } : {}) });
export const mappingStatus = (job) => job.excluded ? "Ignored" : job.role_id || job.new_role ? "Mapped" : "Needs mapping";
export const changedMappings = (snapshot, drafts) => snapshot.jobs
  .filter((job) => JSON.stringify(jobDraft(job)) !== JSON.stringify(drafts[job.uuid]))
  .map((job) => drafts[job.uuid]);
export const draftRoleKey = (role) => `new:${role.department_id}:${role.name}`;
export function roleLabel(draft, roles, departments) {
  const role = draft.new_role || roles.find((r) => r.id === draft.role_id);
  if (!role) return "Needs mapping";
  return `${role.name || "New role"} · ${departments.find((d) => d.id === role.department_id)?.name || "Choose department"}`;
}

// Reuse only unambiguous names; matching ignores case and repeated whitespace,
// but preserves qualifiers such as "Lead" and "Training".
export function reconcileSuggestions(snapshot, drafts, requested, proposed) {
  const normalize = (name) => (name || "").trim().replace(/\s+/g, " ").toLowerCase();
  const candidates = new Map();
  const add = (name, mapping) => {
    const key = normalize(name);
    if (!key) return;
    const target = mapping.role_id ? `role:${mapping.role_id}` : `new:${mapping.new_role.department_id}:${normalize(mapping.new_role.name)}`;
    if (!candidates.has(key)) candidates.set(key, new Map());
    candidates.get(key).set(target, mapping);
  };
  const register = (mapping, jobName) => {
    if (!mapping || mapping.excluded || (!mapping.role_id && !mapping.new_role)) return;
    const role = mapping.new_role || snapshot.roles.find((r) => r.id === mapping.role_id);
    if (!role || !role.name.trim() || !role.department_id) return;
    const target = { role_id: mapping.role_id || null, new_role: mapping.new_role || null };
    add(role.name, target);
    if (jobName) add(jobName, target);
  };
  snapshot.roles.forEach((role) => register({ role_id: role.id }));
  snapshot.jobs.forEach((job) => register(drafts[job.uuid], job.name));
  proposed.filter((s) => s.action !== "review").forEach((s) => register(s, snapshot.jobs.find((j) => j.uuid === s.uuid)?.name));
  const suggestions = new Map(proposed.map((s) => [s.uuid, s]));
  return requested.flatMap((uuid) => {
    const original = suggestions.get(uuid);
    if (original && original.action !== "review") return [original];
    const job = snapshot.jobs.find((j) => j.uuid === uuid);
    const matches = candidates.get(normalize(job?.name));
    if (matches?.size === 1) {
      const target = [...matches.values()][0];
      return [{ uuid, ...target, action: target.new_role ? "create" : "link", reason: "Matches the same job or role name." }];
    }
    return original ? [original] : [];
  });
}

export function suggestedRoleColor(roles) {
  const counts = new Map(ROLE_COLORS.map(([color]) => [color, 0]));
  roles.forEach((role) => {
    if (counts.has(role.color)) counts.set(role.color, counts.get(role.color) + 1);
  });
  return [...counts].sort((a, b) => a[1] - b[1])[0][0];
}

export function withSuggestedColors(snapshot, drafts, proposed) {
  const shared = new Map(Object.values(drafts).filter((d) => !d.excluded && d.new_role)
    .map((d) => [draftRoleKey(d.new_role), d.new_role]));
  const used = [...snapshot.roles, ...shared.values()];
  return proposed.map((suggestion) => {
    if (!suggestion.new_role) return suggestion;
    const key = draftRoleKey(suggestion.new_role);
    const role = shared.get(key) || suggestion.new_role;
    const color = role.color || suggestedRoleColor(used);
    const newRole = { ...role, color };
    if (!shared.has(key)) used.push(newRole);
    shared.set(key, newRole);
    return { ...suggestion, new_role: newRole };
  });
}


export const COVERAGE_OPTIONS = [
  { value: "coverage", label: "Coverage" },
  { value: "admin", label: "Non-coverage · Admin" },
  { value: "training", label: "Non-coverage · Training" },
];
export const savedCoverage = (role) => role?.exclude_from_labor
  ? role.non_coverage_classification || "unclassified" : "coverage";
export const draftCoverage = (draft, roles, drafts) => draft.new_role
  ? draft.new_role.coverage || "coverage"
  : Object.values(drafts).find((d) => !d.excluded && d.role_id === draft.role_id && d.coverage)?.coverage
    || savedCoverage(roles.find((r) => r.id === draft.role_id));

// Coverage is a role setting, shared by every mapped job (including hidden rows).
export function withCoverage(drafts, uuid, coverage, roles) {
  const target = drafts[uuid];
  return Object.fromEntries(Object.entries(drafts).map(([key, draft]) => {
    if (draft.excluded) return [key, draft];
    if (target.new_role && draft.new_role && draftRoleKey(target.new_role) === draftRoleKey(draft.new_role)) {
      const { reason, ...role } = draft.new_role;
      return [key, { ...draft, new_role: { ...role, coverage } }];
    }
    if (target.role_id && draft.role_id === target.role_id) {
      const { coverage: previous, ...next } = draft;
      if (coverage !== savedCoverage(roles.find((r) => r.id === target.role_id))) next.coverage = coverage;
      return [key, next];
    }
    return [key, draft];
  }));
}


export const IMPORT_PERMISSION_OPTIONS = [
  { value: "company_employee", label: "Employee" },
  { value: "company_manager", label: "Manager" },
];
